import { geocodePlaceQuery } from '../../services/placeSearchService';
import { canonicalizeProvinceName } from '../data/parcelFilterContract';
import { NL_SEARCH_CAD_CONTEXT, NL_SEARCH_SHARED_CONTEXT } from '../data/nlSearchConstants';
import {
  flyTargetFromParsedLocation,
  isKnownCadListNlSearchSchema,
  isKnownNlSearchSchema,
  isKnownSharedListNlSearchSchema,
  parsedSearchShouldApplyFilters,
  parsedSearchShouldFly,
  placeNameFromNlQuery,
  sanitizeParsedLotFilters,
  sanitizeParsedSharedListFilters,
} from './nlSearchQuery';
import { sanitizeCadListFilters } from '../../Showroom/utils/cadFilterMatch';

function firstNeighborhoodName(filters) {
  const names = filters?.neighborhood_name;
  if (typeof names === 'string') return names.trim();
  if (!Array.isArray(names)) return '';
  return String(names.find((item) => String(item || '').trim()) || '').trim();
}

function streetNameFromFilters(filters) {
  const value = filters?.street_name;
  return typeof value === 'string' ? value.trim() : '';
}

function provinceNameFromFilters(filters) {
  const names = filters?.province_name;
  if (typeof names === 'string') return names.trim();
  if (!Array.isArray(names)) return '';
  return String(names.find((item) => String(item || '').trim()) || '').trim();
}

/**
 * Chip de barrio o calle + `location: null`: homónimo, cámara quieta.
 * Provincia no entra: si solo hay `province_name`, sí se vuela (Mendoza).
 * @param {object} [filters]
 * @returns {boolean}
 */
function hasNeighborhoodOrStreetFilter(filters) {
  return Boolean(firstNeighborhoodName(filters) || streetNameFromFilters(filters));
}

/**
 * Nominatim del texto libre solo si el parse no nombró lugar y no hay chip
 * de barrio/calle/provincia. Municipio sin chip (`… en juana koslay`) sí.
 * Provincia se geocodifica aparte (nombre canónico, `kind: province`).
 * @param {object} data
 * @returns {boolean}
 */
export function shouldUseNominatimPlaceFallback(data) {
  if (!data || data.intent === 'unknown') return false;
  if (data.location) return false;
  if (hasNeighborhoodOrStreetFilter(data.filters) || provinceNameFromFilters(data.filters)) {
    return false;
  }
  return true;
}

/**
 * Chip de provincia sin barrio/calle: volar a esa provincia.
 * Barrio o `location` (bbox / Nominatim) tienen prioridad.
 * @param {object} data
 * @returns {boolean}
 */
export function shouldFlyToFilteredProvince(data) {
  if (!data || data.intent === 'unknown') return false;
  if (hasNeighborhoodOrStreetFilter(data.filters)) return false;
  return Boolean(provinceNameFromFilters(data.filters));
}

/**
 * Si hay chip de barrio, la cámara es de barrio (no una localidad homónima).
 * Si hay chip de calle, no tratarla como un POI suelto.
 * @param {object} data
 * @returns {string|undefined}
 */
function flyKindFromParse(data) {
  if (firstNeighborhoodName(data?.filters)) return 'neighborhood';
  if (streetNameFromFilters(data?.filters) && data?.location?.kind !== 'address') {
    return 'street';
  }
  return data?.location?.kind;
}

/**
 * Vuelo de `place` / `hybrid`.
 *
 * 1. `location.bbox` `[west, south, east, north]` → `fitBounds`. Polígono del
 *    barrio en DB. Nominatim no conoce nombres catastrales (“Los Quebrachos
 *    Country Golf”). Con bbox **no** se llama a Nominatim.
 * 2. Sin bbox → **una** vez Nominatim con `location.query` tal cual (`kind`).
 *    No se usa el texto del input ni `location.label` (recorte → homónimos OSM,
 *    p. ej. “Chacras del Norte” → Neuquén).
 * 3. Nominatim `[]` → cámara quieta (salvo `center` del parse). Sin reintentos
 *    más cortos ni ítems del dropdown tipado (Enter ≠ esa lista).
 *
 * @param {object} data
 * @param {object} [context]
 * @param {AbortSignal} [context.signal]
 * @returns {Promise<{ lng: number, lat: number, bbox: number[][]|null }|null>}
 */
async function resolveFlyTarget(data, {
  signal,
} = {}) {
  if (!parsedSearchShouldFly(data?.intent) || !data?.location) return null;

  const parsedTarget = flyTargetFromParsedLocation(data.location);
  if (parsedTarget?.bbox) return parsedTarget;

  const contextQuery = typeof data.location.query === 'string'
    ? data.location.query.trim()
    : '';
  if (contextQuery) {
    const kind = flyKindFromParse(data);
    try {
      const geocoded = await geocodePlaceQuery(contextQuery, {
        signal,
        kind,
        contextQuery,
      });
      if (geocoded) return geocoded;
    } catch (err) {
      if (err?.name === 'AbortError') throw err;
    }
  }

  return parsedTarget;
}

/**
 * Si el parse no mandó `location` ni chip de barrio/calle, pero la
 * frase nombra un municipio que Nominatim sí conoce (`… en juana koslay`).
 * Una sola query (cola tras “en”); si Nominatim `[]`, cámara quieta.
 * @param {string} sourceQuery
 * @param {object} [context]
 * @param {AbortSignal} [context.signal]
 * @returns {Promise<{ lng: number, lat: number, bbox: number[][]|null }|null>}
 */
async function resolveNominatimPlaceFallback(sourceQuery, {
  signal,
} = {}) {
  const placeQuery = placeNameFromNlQuery(sourceQuery);
  if (!placeQuery) return null;
  try {
    return await geocodePlaceQuery(placeQuery, { signal, kind: 'locality' });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    return null;
  }
}

/**
 * Vuelo al chip `province_name` cuando no hubo `location` ni barrio/calle.
 * Nominatim con `kind: province` (bbox de la relación, no un pin).
 * @param {object} [filters]
 * @param {object} [context]
 * @param {AbortSignal} [context.signal]
 * @returns {Promise<{ lng: number, lat: number, bbox: number[][]|null }|null>}
 */
async function resolveProvinceFlyTarget(filters, { signal } = {}) {
  const raw = provinceNameFromFilters(filters);
  if (!raw) return null;
  const name = canonicalizeProvinceName(raw) || raw;
  try {
    return await geocodePlaceQuery(name, {
      signal,
      kind: 'province',
      contextQuery: `${name}, Argentina`,
    });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    return null;
  }
}

/**
 * Traduce `data` del parse a acciones de mapa: filtros de lote y/o vuelo.
 * Listing solo si hay `context: shared-list` **y** `schema_version: shared-list-v1`.
 * Sin eso, el sanitizer de Lote tira keys de publicación.
 * Enter/parse no usa el dropdown tipado de Nominatim (`placeResults`).
 *
 * @param {object} data
 * @param {object} [context]
 * @param {AbortSignal} [context.signal]
 * @param {string} [context.parseContext]
 * @param {string[]} [context.listingPriceBandKeys]
 * @param {string} [context.sourceQuery] frase del input (fallback Nominatim)
 * @returns {Promise<{
 *   filtersToApply: Record<string, unknown>|null,
 *   replaceListing: boolean,
 *   flyTo: { lng: number, lat: number, bbox: number[][]|null }|null,
 *   revealFiltered: boolean,
 *   fitFilteredLots: boolean,
 *   schemaBlocked: boolean,
 * }>}
 */
export async function resolveParsedSearchActions(data, {
  signal,
  parseContext = null,
  listingPriceBandKeys = [],
  sourceQuery = '',
} = {}) {
  const isSharedList = parseContext === NL_SEARCH_SHARED_CONTEXT;
  const isCadList = parseContext === NL_SEARCH_CAD_CONTEXT;
  const sharedSchema = isKnownSharedListNlSearchSchema(data?.schema_version);
  const cadSchema = isKnownCadListNlSearchSchema(data?.schema_version);
  const parcelSchema = isKnownNlSearchSchema(data?.schema_version);
  const schemaOk = isCadList
    ? cadSchema
    : (isSharedList ? (sharedSchema || parcelSchema) : parcelSchema);
  const replaceListing = isSharedList && sharedSchema;
  const wantsFilters = parsedSearchShouldApplyFilters(data?.intent);
  const filtersToApply = wantsFilters && schemaOk
    ? (isCadList
      ? sanitizeCadListFilters(data.filters)
      : (replaceListing
        ? sanitizeParsedSharedListFilters(data.filters, { listingPriceBandKeys })
        : sanitizeParsedLotFilters(data.filters)))
    : null;

  let flyTo = null;
  if (!isCadList) {
    flyTo = await resolveFlyTarget(data, { signal });
    if (!flyTo && shouldUseNominatimPlaceFallback(data)) {
      flyTo = await resolveNominatimPlaceFallback(sourceQuery, { signal });
    }
    if (!flyTo && shouldFlyToFilteredProvince(data)) {
      flyTo = await resolveProvinceFlyTarget(data.filters, { signal });
    }
  }
  const hasFilters = Boolean(filtersToApply) && Object.keys(filtersToApply).length > 0;
  const noPlace = !flyTo && !data?.location;
  const revealFiltered = hasFilters && noPlace && !isSharedList && !isCadList;
  const fitFilteredLots = hasFilters && noPlace && isSharedList;

  return {
    filtersToApply,
    replaceListing,
    flyTo,
    revealFiltered,
    fitFilteredLots,
    schemaBlocked: wantsFilters && !schemaOk,
  };
}
